import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RelancesEmailCard } from '@/modules/messagerie/RelancesEmailCard';
import { getSmsSettings, saveSmsSettings, SMS_SETTINGS_DEFAULT, type SmsSettings } from '@/modules/messagerie/api';

const JOURS = [
  { v: 1, l: 'Lun' },
  { v: 2, l: 'Mar' },
  { v: 3, l: 'Mer' },
  { v: 4, l: 'Jeu' },
  { v: 5, l: 'Ven' },
  { v: 6, l: 'Sam' },
  { v: 0, l: 'Dim' },
];

const VARS: Record<string, string> = {
  devis: '{prenom} {numero} {montant}',
  facture: '{prenom} {numero} {montant}',
  rdv: '{prenom} {date} {ville}',
  avis: '{prenom} {lien}',
};

function palierStr(p?: number[]): string {
  return (p ?? []).join(', ');
}
function parsePaliers(s: string): number[] {
  return s
    .split(/[,\s]+/)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function RelancesSettings() {
  const queryClient = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['sms-settings'], queryFn: getSmsSettings });
  const [s, setS] = useState<SmsSettings>(SMS_SETTINGS_DEFAULT);

  useEffect(() => {
    if (settingsQ.data) setS(settingsQ.data);
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () => saveSmsSettings(s),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sms-settings'] });
    },
  });

  const setDecl = (k: keyof SmsSettings['declencheurs'], patch: Partial<SmsSettings['declencheurs']['devis']>) => {
    setS((prev) => ({ ...prev, declencheurs: { ...prev.declencheurs, [k]: { ...prev.declencheurs[k], ...patch } } }));
  };
  const toggleJour = (v: number) => {
    setS((prev) => ({
      ...prev,
      joursOuvres: prev.joursOuvres.includes(v)
        ? prev.joursOuvres.filter((x) => x !== v)
        : [...prev.joursOuvres, v].sort(),
    }));
  };

  if (settingsQ.isLoading) return <p className="text-muted-foreground">Chargement des réglages…</p>;

  return (
    <div className="space-y-6">
      {/* Les e-mails d'abord : Brevo, depuis contact@imptoiture.com. */}
      <RelancesEmailCard />

      {/* Interrupteur maître */}
      <Card>
        <CardHeader>
          <CardTitle>Relances automatiques</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <input
              id="sms-actif"
              type="checkbox"
              checked={s.actif}
              onChange={(e) => {
                setS((p) => ({ ...p, actif: e.target.checked }));
              }}
              className="mt-1 h-4 w-4"
            />
            <label htmlFor="sms-actif">
              <span className="font-medium">Activer l&apos;envoi automatique des relances SMS</span>
              <span className="block text-sm text-muted-foreground">
                À n&apos;activer qu&apos;une fois l&apos;opérateur SMS branché (numéro + clé) et vérifié. Tant que
                c&apos;est décoché, aucun SMS automatique n&apos;est envoyé — la messagerie manuelle reste disponible.
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Déclencheurs */}
      <Card>
        <CardHeader>
          <CardTitle>Déclencheurs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DeclLine
            titre="Devis sans réponse"
            desc="Relance X jours après l'envoi d'un devis resté sans réponse."
            actif={s.declencheurs.devis.actif}
            onActif={(v) => {
              setDecl('devis', { actif: v });
            }}
            paliers={palierStr(s.declencheurs.devis.paliers)}
            onPaliers={(v) => {
              setDecl('devis', { paliers: parsePaliers(v) });
            }}
          />
          <DeclLine
            titre="Facture impayée"
            desc="Relance X jours après l'échéance de paiement dépassée."
            actif={s.declencheurs.facture.actif}
            onActif={(v) => {
              setDecl('facture', { actif: v });
            }}
            paliers={palierStr(s.declencheurs.facture.paliers)}
            onPaliers={(v) => {
              setDecl('facture', { paliers: parsePaliers(v) });
            }}
          />
          <DeclLine
            titre="Rappel de RDV / intervention"
            desc="SMS la veille d'un chantier planifié."
            actif={s.declencheurs.rdv.actif}
            onActif={(v) => {
              setDecl('rdv', { actif: v });
            }}
          />
          <DeclLine
            titre="Demande d'avis Google"
            desc="SMS après un chantier terminé récemment (avec le lien d'avis)."
            actif={s.declencheurs.avis.actif}
            onActif={(v) => {
              setDecl('avis', { actif: v });
            }}
          />
        </CardContent>
      </Card>

      {/* Créneau d'envoi */}
      <Card>
        <CardHeader>
          <CardTitle>Créneau d&apos;envoi</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hd">De (heure)</Label>
              <Input
                id="hd"
                type="number"
                min={0}
                max={23}
                value={s.heureDebut}
                onChange={(e) => {
                  setS((p) => ({ ...p, heureDebut: parseInt(e.target.value, 10) || 0 }));
                }}
                className="w-24"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hf">À (heure)</Label>
              <Input
                id="hf"
                type="number"
                min={1}
                max={24}
                value={s.heureFin}
                onChange={(e) => {
                  setS((p) => ({ ...p, heureFin: parseInt(e.target.value, 10) || 0 }));
                }}
                className="w-24"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Jours</Label>
              <div className="flex flex-wrap gap-1.5">
                {JOURS.map((j) => (
                  <button
                    key={j.v}
                    type="button"
                    onClick={() => {
                      toggleJour(j.v);
                    }}
                    className={`h-12 rounded-md border px-3 text-sm font-medium lg:h-11 ${
                      s.joursOuvres.includes(j.v)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground'
                    }`}
                  >
                    {j.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Fuseau Europe/Paris. Aucun SMS automatique n&apos;est envoyé en dehors de ce créneau.
          </p>
        </CardContent>
      </Card>

      {/* Lien avis + Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Messages types</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="lien">Lien d&apos;avis Google</Label>
            <Input
              id="lien"
              value={s.lienAvis}
              onChange={(e) => {
                setS((p) => ({ ...p, lienAvis: e.target.value }));
              }}
            />
          </div>
          {(['devis', 'facture', 'rdv', 'avis'] as const).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`tpl-${k}`} className="capitalize">
                {k}
              </Label>
              <Textarea
                id={`tpl-${k}`}
                value={s.templates[k]}
                onChange={(e) => {
                  setS((p) => ({ ...p, templates: { ...p.templates, [k]: e.target.value } }));
                }}
                rows={3}
              />
              <p className="text-[11px] text-muted-foreground">Variables : {VARS[k]}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={() => {
            save.mutate();
          }}
          disabled={save.isPending}
        >
          <Save className="h-4 w-4" />
          {save.isPending ? 'Enregistrement…' : 'Enregistrer les réglages'}
        </Button>
        {save.isSuccess ? <span className="text-sm text-green-600">Enregistré ✓</span> : null}
        {save.isError ? (
          <span className="text-sm text-destructive">{messageErreur(save.error)}</span>
        ) : null}
      </div>
    </div>
  );
}

function DeclLine(props: {
  titre: string;
  desc: string;
  actif: boolean;
  onActif: (v: boolean) => void;
  paliers?: string;
  onPaliers?: (v: string) => void;
}) {
  const cbId = `decl-${props.titre.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-2 border-b pb-4 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <input
          id={cbId}
          type="checkbox"
          checked={props.actif}
          onChange={(e) => {
            props.onActif(e.target.checked);
          }}
          className="mt-1 h-4 w-4"
        />
        <label htmlFor={cbId}>
          <span className="font-medium">{props.titre}</span>
          <span className="block text-sm text-muted-foreground">{props.desc}</span>
        </label>
      </div>
      {props.onPaliers ? (
        <div className="flex items-center gap-2 pl-7 sm:pl-0">
          <Label htmlFor={`pal-${props.titre}`} className="text-xs text-muted-foreground">
            Jours
          </Label>
          <Input
            id={`pal-${props.titre}`}
            value={props.paliers}
            onChange={(e) => {
              props.onPaliers?.(e.target.value);
            }}
            placeholder="7, 14"
            className="w-28"
          />
        </div>
      ) : null}
    </div>
  );
}
